import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { useConfirm } from '../components/ConfirmDialog';
import { Card, Spinner, ErrorNote, Empty } from '../components/ui';
import { fmtDateTime } from '../lib/format';

export default function EmailLog() {
  const { toast, run } = useApp();
  const [ask, confirmDialog] = useConfirm();
  const { data, error, loading, reload } = useAsync(() => api.get('/email-log'), []);
  const [openId, setOpenId] = useState(null);

  if (loading) return <Spinner label="Loading email history" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;

  const { status, entries } = data;

  const clear = async () => {
    if (!await ask({ title: 'Clear email history?', body: 'Every logged email will be removed from this list. Emails already sent are not affected.', confirmLabel: 'Clear history' })) return;
    run(async () => { await api.del('/email-log'); toast('Cleared'); reload(); });
  };

  return (
    <>
      <PageHeader
        title="Email history"
        sub={status.mode === 'demo' ? 'Demo mode — mail is captured, not sent.' : `Sending live via ${status.mode} from ${status.from}`}
        back="/settings"
        action={<>
          <Link className="act" to="/email/new">Compose</Link>
          {entries.length ? <button className="act" onClick={clear}>Clear</button> : null}
        </>}
      />
      <div className="view">
        <div className="col" style={{ gap: 'var(--s-2)' }}>
          {entries.map(e => {
            const open = openId === e.id;
            return (
              <Card key={e.id} tight>
                <button
                  className="row between"
                  onClick={() => setOpenId(open ? null : e.id)}
                  style={{ width: '100%', background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}
                >
                  <div className="col" style={{ gap: 2 }}>
                    <span className="nm" style={{ fontSize: 'var(--fs-sm)' }}>{e.subject || '(no subject)'}</span>
                    <span className="sb">To {e.to}</span>
                  </div>
                  <div className="col" style={{ gap: 2, alignItems: 'flex-end' }}>
                    <span className="sb">{fmtDateTime(e.at)}</span>
                    <span style={{ color: e.delivered ? 'var(--green-700)' : 'var(--red-600)', fontWeight: 700, fontSize: 'var(--fs-sm)' }}>
                      {e.delivered ? 'delivered' : 'failed'}
                    </span>
                  </div>
                </button>
                {open ? (
                  <div style={{ marginTop: 'var(--s-3)', paddingTop: 'var(--s-3)', borderTop: '1px solid var(--border)' }}>
                    <div className="sb">From {e.from}{e.replyTo && e.replyTo !== e.from ? ` · Reply-to ${e.replyTo}` : ''}</div>
                    {e.attachmentCount ? <div className="sb">{e.attachmentCount} attachment{e.attachmentCount === 1 ? '' : 's'}</div> : null}
                    {e.messageId ? <div className="sb">Message ID {e.messageId}</div> : null}
                    {e.error ? <div className="sb" style={{ color: 'var(--red-600)' }}>Error: {e.error}</div> : null}
                    {e.html ? (
                      <iframe
                        title={`Email preview ${e.id}`}
                        srcDoc={e.html}
                        style={{ width: '100%', height: 420, border: '1px solid var(--border)', borderRadius: 'var(--r-3)', marginTop: 'var(--s-2)', background: '#fff' }}
                      />
                    ) : null}
                  </div>
                ) : null}
              </Card>
            );
          })}
          {!entries.length ? <Empty>No emails sent yet.</Empty> : null}
        </div>
      </div>
      {confirmDialog}
    </>
  );
}
